import React, {useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";
import {carsTheme} from "@rw/themes";

import {IStore} from "../../app/reducers/hybrid_reducer";

const validateFile = (file: File) => {
    const errors: string[] = [];
    if (file.size > 5_000_000) {
        errors.push("Maksymalny rozmiar to 5 MB.");
    }
    if (!/image\/(jpg|jpeg|png|gif|webp)$/i.test(file.type)) {
        errors.push("Dozwolone formaty: JPG, PNG, GIF, WEBP.");
    }
    return errors;
};

type ProfileImageUploadProps = {
    label: string;
    hint?: string;
    previewUrl: string;
    imageKey: string;
    aspect?: "logo" | "cover";
    onChange: (next: {url: string; key: string}) => void;
};

export const ProfileImageUpload = ({label, hint, previewUrl, imageKey, aspect = "logo", onChange}: ProfileImageUploadProps) => {
    const userData = useSelector((store: IStore) => store.dashboard.userData);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const uploadFile = async (file: File) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("userID", userData?.data?.user_id?.toString() || "");

        const response = await fetch("https://rw-web-api.eu/wp-json/wp/v2/upload_gallery_to_s3/", {
            method: "POST",
            body: formData
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.filename) {
            throw new Error(data?.message || "Upload nie powiódł się");
        }
        return {
            key: String(data.filename),
            url: String(data.url || "")
        };
    };

    const deleteKey = async (key: string) => {
        if (!key) {
            return;
        }
        const formData = new FormData();
        formData.append("key", key);
        await fetch("https://rw-web-api.eu/wp-json/wp/v2/delete_from_s3/", {
            method: "POST",
            body: formData
        }).catch(() => undefined);
    };

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) {
            return;
        }
        const fileErrors = validateFile(file);
        if (fileErrors.length) {
            setError(fileErrors[0]);
            return;
        }

        setUploading(true);
        setError(null);
        try {
            if (imageKey) {
                await deleteKey(imageKey);
            }
            const uploaded = await uploadFile(file);
            onChange(uploaded);
        } catch (err) {
            console.warn("profile image upload", err);
            setError("Nie udało się wgrać zdjęcia.");
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        setUploading(true);
        setError(null);
        try {
            await deleteKey(imageKey);
            onChange({url: "", key: ""});
        } catch {
            setError("Nie udało się usunąć zdjęcia.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div css={wrap}>
            <label css={labelStyle}>{label}</label>
            {hint ? <p css={hintStyle}>{hint}</p> : null}
            <div css={previewRow}>
                <div css={aspect === "cover" ? coverBox : logoBox}>
                    {previewUrl ? <img src={previewUrl} alt="" css={imgStyle} /> : <span css={placeholder}>Brak zdjęcia</span>}
                </div>
                <div css={actions}>
                    <label css={fileBtn}>
                        {uploading ? "Wgrywanie…" : previewUrl ? "Zmień" : "Dodaj"}
                        <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileInput} disabled={uploading} hidden />
                    </label>
                    {previewUrl ? (
                        <button type="button" css={removeBtn} onClick={handleRemove} disabled={uploading}>
                            Usuń
                        </button>
                    ) : null}
                </div>
            </div>
            {error ? <p css={errorStyle}>{error}</p> : null}
        </div>
    );
};

const wrap = css`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
`;

const labelStyle = css`
    font-size: 14px;
    font-weight: 700;
`;

const hintStyle = css`
    margin: 0;
    font-size: 13px;
    color: #66676c;
`;

const previewRow = css`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 1rem;
`;

const logoBox = css`
    width: 120px;
    height: 120px;
    border: 1px solid #dcdde0;
    border-radius: 8px;
    overflow: hidden;
    background: #f5f5f6;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const coverBox = css`
    width: min(100%, 360px);
    height: 140px;
    border: 1px solid #dcdde0;
    border-radius: 8px;
    overflow: hidden;
    background: #f5f5f6;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const imgStyle = css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const placeholder = css`
    font-size: 13px;
    color: #888;
`;

const actions = css`
    display: flex;
    gap: 0.5rem;
    align-items: center;
`;

const fileBtn = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 14px;
    border: 1px solid ${carsTheme.colors.brand_primary};
    border-radius: 4px;
    color: ${carsTheme.colors.brand_primary};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    background: #fff;

    &:hover {
        background: #fff7f2;
    }
`;

const removeBtn = css`
    padding: 8px 14px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: #fff;
    font-size: 14px;
    cursor: pointer;

    &:hover {
        border-color: #c62828;
        color: #c62828;
    }
`;

const errorStyle = css`
    margin: 0;
    color: #c62828;
    font-size: 13px;
`;
